// @ts-check

// Vendor imports
const React = require('react')
// Client V2 common imports
const useClientEffect = require('client-v2/common/hooks/client-effect')
const ViewportObserver = require('client-v2/common/utils/viewport-observer')

/**
 * @typedef {Object} ViewportObserverEntryExtra
 * @property {() => void} onVisible
 * @property {() => void} onHidden
 */

/**
 * @typedef {Object} UsePresetViewportObserverOptions
 * @property {React.RefObject<Element>} options.elementRef
 * @property {boolean} [options.initialIsVisible]
 * @property {boolean} [options.isDisabled]
 * @property {boolean} [options.unobserveOnVisible]
 * @property {() => void} [options.onHidden]
 * @property {() => void} [options.onVisible]
 */

/** @type {IntersectionObserverInit} */
const VISIBLE_10_PCT_INTERSECTION_OBSERVER_OPTIONS = { threshold: 0.10 }
/** @type {IntersectionObserverInit} */
const VISIBLE_50_PCT_INTERSECTION_OBSERVER_OPTIONS = { threshold: 0.50 }
/** @type {IntersectionObserverInit} */
const VISIBLE_90_PCT_INTERSECTION_OBSERVER_OPTIONS = { threshold: 0.90 }
/** @type {IntersectionObserverInit} */
const VISIBLE_100_PCT_INTERSECTION_OBSERVER_OPTIONS = { threshold: 1.00 }

// ViewportObserver cache

/** @type {WeakMap<IntersectionObserverInit, ViewportObserver>} */
const viewportObserverCache = new WeakMap()

/**
 * @param {IntersectionObserverInit} intersectionObserverOptions
 * @returns {ViewportObserver}
 */
function getViewportObserver(intersectionObserverOptions) {
  const cached = viewportObserverCache.get(intersectionObserverOptions)
  if (cached) {
    return cached
  }
  /**
   * @param {Element} _element
   * @param {ViewportObserverEntryExtra} onVisible
   */
  function visibleCallback(_element, { onVisible }) { onVisible() }
  /**
   * @param {Element} _element
   * @param {ViewportObserverEntryExtra} onVisible
   */
  function hiddenCallback(_element, { onHidden }) { onHidden() }
  const created = new ViewportObserver({
    intersectionObserverOptions,
    visibleCallback,
    hiddenCallback
  })
  viewportObserverCache.set(intersectionObserverOptions, created)
  return created
}

// Hook definition

/**
 * @param {Object} options
 * @param {React.RefObject<Element>} options.elementRef
 * @param {boolean} [options.initialIsVisible]
 * @param {IntersectionObserverInit} options.intersectionObserverOptions
 * @param {boolean} [options.isDisabled]
 * @param {boolean} [options.unobserveOnVisible]
 * @param {() => void} [options.onHidden]
 * @param {() => void} [options.onVisible]
 * @returns {[ boolean ]}
 */
function useViewportObserver({
  elementRef,
  initialIsVisible = false,
  intersectionObserverOptions,
  isDisabled = false,
  unobserveOnVisible = false,
  onHidden,
  onVisible
}) {
  const [ isVisible, setVisible ] = React.useState(initialIsVisible)
  const handleVisible = React.useCallback(
    () => {
      if (!isVisible) {
        setVisible(true)
        if (onVisible) {
          onVisible()
        }
      }
    },
    [ isVisible, setVisible, onVisible ]
  )
  const handleHidden = React.useCallback(
    () => {
      if (isVisible) {
        setVisible(false)
        if (onHidden) {
          onHidden()
        }
      }
    },
    [ isVisible, setVisible, onHidden ]
  )
  useClientEffect(
    () => {
      const el = elementRef.current
      if (isDisabled || !el) {
        return
      }
      const viewportObserver = getViewportObserver(intersectionObserverOptions)
      viewportObserver.observe(el, {
        onVisible() {
          handleVisible()
          if (unobserveOnVisible) {
            viewportObserver.unobserve(el)
          }
        },
        onHidden() {
          handleHidden()
        }
      })
      return () => {
        viewportObserver.unobserve(el)
      }
    },
    [
      elementRef, isDisabled, intersectionObserverOptions,
      unobserveOnVisible, handleVisible, handleHidden
    ]
  )
  return [ isVisible ]
}

/**
 * @param {UsePresetViewportObserverOptions} options
 * @returns {[ boolean ]}
 */
function use10PctVisibleViewportObserver(options) {
  return useViewportObserver({
    ...options,
    intersectionObserverOptions: VISIBLE_10_PCT_INTERSECTION_OBSERVER_OPTIONS
  })
}

/**
 * @param {UsePresetViewportObserverOptions} options
 * @returns {[ boolean ]}
 */
function use50PctVisibleViewportObserver(options) {
  return useViewportObserver({
    ...options,
    intersectionObserverOptions: VISIBLE_50_PCT_INTERSECTION_OBSERVER_OPTIONS
  })
}

/**
 * @param {UsePresetViewportObserverOptions} options
 * @returns {[ boolean ]}
 */
function use90PctVisibleViewportObserver(options) {
  return useViewportObserver({
    ...options,
    intersectionObserverOptions: VISIBLE_90_PCT_INTERSECTION_OBSERVER_OPTIONS
  })
}

/**
 * @param {UsePresetViewportObserverOptions} options
 * @returns {[ boolean ]}
 */
function use100PctVisibleViewportObserver(options) {
  return useViewportObserver({
    ...options,
    intersectionObserverOptions: VISIBLE_100_PCT_INTERSECTION_OBSERVER_OPTIONS
  })
}


module.exports = {
  use10PctVisibleViewportObserver,
  use50PctVisibleViewportObserver,
  use90PctVisibleViewportObserver,
  use100PctVisibleViewportObserver,
  useViewportObserver
}
